import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';

import { Portal } from '@mui/material';
import { differenceInMinutes } from 'date-fns';
import { useMediaQueries, useReferenceContext, useStore } from 'hooks';

import DetailsDrawer from 'components/shared/SystemOffline/DetailsDrawer';
import { SYSTEM_STATUSES } from 'models/hardwareStatusStore';

import Desktop from './desktop';
import Mobile from './mobile';

const REFRESH_TIME = 300;
const INITIAL_TIME = 5;

const SystemOfflineBanner = () => {
  const [refreshTimeLeft, setRefreshTimeLeft] = useState(REFRESH_TIME);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [onlineCountdown, setOnlineCountdown] = useState(INITIAL_TIME);
  const [isBackOnline, setIsBackOnline] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const { hardwareStatusStore, topologyManagementStore, establishmentStore } = useStore();
  const { systemOfflineBanner } = useReferenceContext();
  const { isMobileOrSmaller, isTabletOrGreater } = useMediaQueries();

  const closeBanner = useCallback(() => {
    hardwareStatusStore.setSystemStatus(SYSTEM_STATUSES.online, true);
  }, [hardwareStatusStore]);

  useEffect(() => {
    let timer = null;

    if (hardwareStatusStore.isRestored) {
      clearInterval(timer);
      setIsRefreshing(false);
      return;
    }

    if (isRefreshing && refreshTimeLeft > 0) {
      timer = setInterval(() => {
        setRefreshTimeLeft(prevTime => prevTime - 1);
      }, 1000);
    } else if (refreshTimeLeft === 0) {
      if (
        !establishmentStore?.establishment?.systemLastUpdated ||
        differenceInMinutes(
          new Date(),
          new Date(establishmentStore?.establishment?.systemLastUpdated),
        ) >= 5
      ) {
        topologyManagementStore.fetchEstablishmentsSensors();
      }
      clearInterval(timer);
      setIsRefreshing(false);
      setRefreshTimeLeft(REFRESH_TIME);
    }
    return () => clearInterval(timer);
  }, [
    isRefreshing,
    refreshTimeLeft,
    hardwareStatusStore.isRestored,
    topologyManagementStore,
    establishmentStore?.establishment?.systemLastUpdated,
    hardwareStatusStore.isPartiallyRestored,
  ]);

  useEffect(() => {
    let timer = null;

    if (isBackOnline && onlineCountdown > 0) {
      timer = setInterval(() => {
        setOnlineCountdown(prevTime => prevTime - 1);
      }, 1000);
    } else if (onlineCountdown === 0) {
      clearInterval(timer);
      setIsBackOnline(false);
      setOnlineCountdown(INITIAL_TIME);
      closeBanner();
    }

    return () => clearInterval(timer);
  }, [isBackOnline, onlineCountdown, hardwareStatusStore.isRestored, closeBanner]);

  const startRefreshCountdown = useCallback(async () => {
    if (isRefreshing) return;

    try {
      if (refreshTimeLeft === 0) {
        setRefreshTimeLeft(REFRESH_TIME);
      }
      if (!hardwareStatusStore.isRestored) {
        setIsRefreshing(true);
      }

      await hardwareStatusStore.checkHardwareStatus();
      await topologyManagementStore.fetchEstablishmentsSensors();
    } catch (err) {
      console.error(err);
    }
  }, [hardwareStatusStore, topologyManagementStore, refreshTimeLeft, isRefreshing]);

  useEffect(() => {
    if (hardwareStatusStore.isRestored) {
      setIsBackOnline(true);
    }
  }, [hardwareStatusStore.isRestored]);

  const closeDetailsDrawer = () => {
    setDetailsDrawerOpen(!detailsDrawerOpen);
  };

  const handleDetailsClick = () => {
    setDetailsDrawerOpen(!detailsDrawerOpen);
  };

  const handleNavigateToHardwareManagement = () => {
    navigate('/system-configuration/topology-management');
  };

  if (hardwareStatusStore.isOnline || process.env.REACT_APP_OFFLINE_MODE_DISABLED === 'true') {
    return;
  }

  return (
    <>
      <Portal container={systemOfflineBanner.ref.current}>
        {isTabletOrGreater && (
          <Desktop
            closeBanner={closeBanner}
            refreshTimeLeft={refreshTimeLeft}
            isRefreshing={isRefreshing}
            handleRefresh={startRefreshCountdown}
            onlineCountdown={onlineCountdown}
            isBackOnline={isBackOnline}
            handleDetailsClick={handleDetailsClick}
          />
        )}
        {isMobileOrSmaller && (
          <Mobile
            closeBanner={closeBanner}
            handleRefresh={startRefreshCountdown}
            isRefreshing={isRefreshing}
            refreshTimeLeft={refreshTimeLeft}
            onlineCountdown={onlineCountdown}
            isBackOnline={isBackOnline}
            handleDetailsClick={handleDetailsClick}
          />
        )}
      </Portal>
      <DetailsDrawer
        handleClose={closeDetailsDrawer}
        isOpen={detailsDrawerOpen}
        handleNavigateToHardwareManagement={handleNavigateToHardwareManagement}
      />
    </>
  );
};

export default observer(SystemOfflineBanner);
